import { type Caption } from '@remotion/captions';

interface CaptionBoundaryTimingItem {
  from: number;
  durationInFrames: number;
  captionStartInSeconds: number;
}

interface CaptionBoundaryTimingAsset {
  captions: Caption[];
  videoStart?: number;
  videoEnd?: number;
}

interface AdjustCaptionBoundaryTimingParams<
  TItem extends CaptionBoundaryTimingItem,
  TAsset extends CaptionBoundaryTimingAsset,
> {
  item: TItem;
  asset: TAsset;
  fps: number;
  edge: 'left' | 'right';
  framePosition: number;
}

interface AdjustCaptionBoundaryTimingResult<
  TItem extends CaptionBoundaryTimingItem,
  TAsset extends CaptionBoundaryTimingAsset,
> {
  item: TItem;
  asset: TAsset;
}

const frameToMs = (frame: number, fps: number): number =>
  Math.round((frame / fps) * 1000);

export const adjustCaptionBoundaryTiming = <
  TItem extends CaptionBoundaryTimingItem,
  TAsset extends CaptionBoundaryTimingAsset,
>({
  item,
  asset,
  fps,
  edge,
  framePosition,
}: AdjustCaptionBoundaryTimingParams<
  TItem,
  TAsset
>): AdjustCaptionBoundaryTimingResult<TItem, TAsset> => {
  if (asset.captions.length === 0) {
    return { item, asset };
  }

  if (edge === 'left') {
    const itemEnd = item.from + item.durationInFrames;
    const firstCaptionStartMs = asset.captions[0]?.startMs;
    const firstCaptionStartFrame =
      typeof firstCaptionStartMs === 'number' &&
      Number.isFinite(firstCaptionStartMs)
        ? Math.max(0, Math.floor((firstCaptionStartMs / 1000) * fps))
        : itemEnd - 1;
    const nextFrom = Math.min(
      Math.max(framePosition, 0),
      itemEnd - 1,
      firstCaptionStartFrame,
    );
    const nextStartMs = frameToMs(nextFrom, fps);

    return {
      item: {
        ...item,
        from: nextFrom,
        durationInFrames: itemEnd - nextFrom,
        captionStartInSeconds: nextStartMs / 1000,
      },
      asset: {
        ...asset,
        videoStart: nextStartMs,
      },
    };
  }

  const nextEndFrame = Math.max(framePosition, item.from + 1);
  const requestedEndMs = frameToMs(nextEndFrame, fps);
  const lastIndex = asset.captions.length - 1;
  const lastCaption = asset.captions[lastIndex];
  const effectiveEndMs = Math.max(
    requestedEndMs,
    lastCaption?.endMs ?? requestedEndMs,
    (lastCaption?.startMs ?? requestedEndMs) + 1,
  );
  const effectiveEndFrame = Math.max(
    item.from + 1,
    Math.ceil((effectiveEndMs / 1000) * fps),
  );
  return {
    item: {
      ...item,
      durationInFrames: effectiveEndFrame - item.from,
    },
    asset: {
      ...asset,
      videoEnd: effectiveEndMs,
    },
  };
};
